import { useState } from 'react';
import {
  Box,
  ButtonBase,
  Dialog,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import Barcode from 'react-barcode';
import { getPackTypeLabel } from '../../constants/packTypes';

interface Props {
  // The package sticker code (e.g. SO-157537-01) — the Locus scanId the rider scans at pickup.
  code: string;
  packType: string;
  // Shows a delete button when provided; omit for read-only views.
  onRemove?: () => void;
}

export const PackageBarcodeCard = ({ code, packType, onRemove }: Props) => {
  const [isZoomed, setIsZoomed] = useState(false);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const packTypeLabel = getPackTypeLabel(packType);

  return (
    <Box border={1} borderColor="divider" borderRadius={1} p={1} sx={{ maxWidth: '100%' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={1} minHeight={30}>
        <Typography variant="body2" fontWeight={600}>
          {packTypeLabel}
        </Typography>
        {onRemove && (
          <IconButton size="small" aria-label={`Remove package ${code}`} onClick={onRemove}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      {/* White background keeps the barcode scannable in dark mode. */}
      <ButtonBase
        onClick={() => setIsZoomed(true)}
        aria-label={`Enlarge barcode ${code}`}
        title="Click to enlarge"
        sx={{
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
          bgcolor: '#fff',
          borderRadius: 0.5,
          overflowX: 'auto',
        }}
      >
        <Barcode
          value={code}
          format="CODE128"
          height={50}
          width={1.5}
          fontSize={14}
          margin={6}
          background="#ffffff"
          lineColor="#000000"
        />
        <ZoomInIcon
          fontSize="small"
          sx={{ position: 'absolute', top: 2, right: 2, color: 'grey.600' }}
        />
      </ButtonBase>

      {/* Full-screen, plain white view so a phone camera can scan the barcode off a monitor.
          A fallback for QA/support — production pickup scans the physical sticker. */}
      <Dialog
        fullScreen
        open={isZoomed}
        onClose={() => setIsZoomed(false)}
        slotProps={{ paper: { sx: { bgcolor: '#fff', color: '#000' } } }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" px={2} py={1}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#000' }}>
            {packTypeLabel}
          </Typography>
          <IconButton
            aria-label="Close enlarged barcode"
            onClick={() => setIsZoomed(false)}
            sx={{ color: '#000' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        {/* Clicking anywhere on the barcode area also closes, so it's quick to dismiss. */}
        <Box
          flex={1}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          gap={2}
          px={2}
          onClick={() => setIsZoomed(false)}
          sx={{ cursor: 'zoom-out' }}
        >
          <Box maxWidth="100%" sx={{ overflowX: 'auto' }}>
            <Barcode
              value={code}
              format="CODE128"
              height={isSmallScreen ? 120 : 200}
              width={isSmallScreen ? 2 : 4}
              displayValue={false}
              margin={16}
              background="#ffffff"
              lineColor="#000000"
            />
          </Box>
          <Typography
            sx={{ color: '#000', fontFamily: 'monospace', fontWeight: 700, letterSpacing: 2 }}
            fontSize={isSmallScreen ? 24 : 40}
          >
            {code}
          </Typography>
        </Box>
      </Dialog>
    </Box>
  );
};
